import { useState, useEffect, useRef, Fragment } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Dialog, Transition } from '@headlessui/react'
import {
  BellIcon,
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import { BellAlertIcon } from '@heroicons/react/24/solid'
import {
  getAlerts,
  getAlertsSummary,
  acknowledgeAlert,
  resolveAlert,
  Alert,
  AlertsSummary,
} from '@/api/alerts'
import { useWebSocket } from '@/context/WebSocketContext'

const severityConfig = {
  critical: { color: 'bg-red-500', text: 'text-red-500', label: 'Critique' },
  high: { color: 'bg-orange-500', text: 'text-orange-500', label: 'Élevé' },
  medium: { color: 'bg-yellow-500', text: 'text-yellow-500', label: 'Moyen' },
  low: { color: 'bg-blue-500', text: 'text-blue-500', label: 'Faible' },
}

function formatTime(dateStr: string) {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)
  if (diffMins < 1) return "À l'instant"
  if (diffMins < 60) return `Il y a ${diffMins} min`
  if (diffHours < 24) return `Il y a ${diffHours}h`
  return `Il y a ${diffDays}j`
}

interface AlertsListProps {
  alerts: Alert[]
  summary: AlertsSummary | undefined
  unreadCount: number
  onClose: () => void
  onAck: (id: string) => void
  onResolve: (id: string) => void
  ackPending: boolean
  resolvePending: boolean
}

function AlertsList({ alerts, summary, unreadCount, onClose, onAck, onResolve, ackPending, resolvePending }: AlertsListProps) {
  return (
    <>
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <ExclamationTriangleIcon className="w-5 h-5 text-orange-500 flex-shrink-0" />
          <h3 className="font-semibold text-gray-900 dark:text-white truncate">
            Alertes actives
          </h3>
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded-full flex-shrink-0">
              {unreadCount}
            </span>
          )}
        </div>
        <Link
          to="/alerts"
          onClick={onClose}
          className="text-sm text-blue-500 hover:text-blue-600 dark:text-blue-400 flex-shrink-0 ml-2"
        >
          Voir tout
        </Link>
      </div>

      {/* Alerts list */}
      <div className="flex-1 overflow-y-auto sm:max-h-[400px]">
        {alerts.length === 0 ? (
          <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
            <BellIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Aucune alerte active</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {alerts.map((alert) => {
              const config = severityConfig[alert.severity] || severityConfig.low
              const isAcknowledged = !!alert.acknowledged_at

              return (
                <div
                  key={alert.id}
                  className={`px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                    !isAcknowledged ? 'bg-orange-50/50 dark:bg-orange-900/10' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-2 h-2 mt-2 rounded-full ${config.color} flex-shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-medium ${config.text}`}>
                          {config.label}
                        </span>
                        <span className="text-xs text-gray-400">
                          {formatTime(alert.triggered_at)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-900 dark:text-white truncate">
                        {alert.alert_type.replace(/_/g, ' ')}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {alert.machine_id}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {!isAcknowledged && (
                        <button
                          onClick={() => onAck(alert.id)}
                          disabled={ackPending}
                          className="p-2 text-gray-400 hover:text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
                          title="Marquer comme vu"
                        >
                          <CheckIcon className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => onResolve(alert.id)}
                        disabled={resolvePending}
                        className="p-2 text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                        title="Résoudre"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Footer with summary */}
      {summary && unreadCount > 0 && (
        <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex-shrink-0">
          <div className="flex items-center justify-center gap-4 text-xs flex-wrap">
            {summary.by_severity?.critical > 0 && (
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-red-500" />
                <span className="text-gray-600 dark:text-gray-400">
                  {summary.by_severity.critical} critique{summary.by_severity.critical > 1 ? 's' : ''}
                </span>
              </span>
            )}
            {summary.by_severity?.high > 0 && (
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-orange-500" />
                <span className="text-gray-600 dark:text-gray-400">
                  {summary.by_severity.high} élevé{summary.by_severity.high > 1 ? 's' : ''}
                </span>
              </span>
            )}
            {summary.by_severity?.medium > 0 && (
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-yellow-500" />
                <span className="text-gray-600 dark:text-gray-400">
                  {summary.by_severity.medium} moyen{summary.by_severity.medium > 1 ? 's' : ''}
                </span>
              </span>
            )}
          </div>
        </div>
      )}
    </>
  )
}

export function AlertsPanel() {
  const [isOpen, setIsOpen] = useState(false)
  const [hasNewAlert, setHasNewAlert] = useState(false)
  // Track viewport so the mobile Dialog is only MOUNTED on mobile. When it
  // was merely hidden with `sm:hidden`, Headless UI still treated any click
  // inside the desktop dropdown as an outside-click and closed the panel.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && !window.matchMedia('(min-width: 640px)').matches
  )
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 640px)')
    const onChange = (e: MediaQueryListEvent) => setIsMobile(!e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  const panelRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()
  const { onNewAlert } = useWebSocket()

  useEffect(() => {
    const unsubscribe = onNewAlert(() => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] })
      setHasNewAlert(true)
      setTimeout(() => setHasNewAlert(false), 5000)
    })
    return unsubscribe
  }, [onNewAlert, queryClient])

  const { data: summary } = useQuery<AlertsSummary>({
    queryKey: ['alerts', 'summary'],
    queryFn: getAlertsSummary,
    refetchInterval: 30000,
  })

  const { data: alerts = [] } = useQuery<Alert[]>({
    queryKey: ['alerts', 'unresolved'],
    queryFn: () => getAlerts({ resolved: false, limit: 20 }),
    refetchInterval: 30000,
    enabled: isOpen,
  })

  const ackMutation = useMutation({
    mutationFn: (id: string) => acknowledgeAlert(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] })
    },
  })

  const resolveMutation = useMutation({
    mutationFn: (id: string) => resolveAlert(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] })
    },
  })

  // Close desktop dropdown on outside click (mobile drawer handled by Dialog)
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen && typeof window !== 'undefined' && window.matchMedia('(min-width: 640px)').matches) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const unreadCount = summary?.total_unresolved || 0
  const hasCritical = (summary?.by_severity?.critical || 0) > 0
  const shouldPulse = hasCritical || hasNewAlert

  const listProps = {
    alerts,
    summary,
    unreadCount,
    onClose: () => setIsOpen(false),
    onAck: (id: string) => ackMutation.mutate(id),
    onResolve: (id: string) => resolveMutation.mutate(id),
    ackPending: ackMutation.isPending,
    resolvePending: resolveMutation.isPending,
  }

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell button with badge */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        title={`${unreadCount} alerte${unreadCount > 1 ? 's' : ''} active${unreadCount > 1 ? 's' : ''}`}
      >
        {shouldPulse ? (
          <BellAlertIcon className="w-6 h-6 text-red-500 animate-pulse" />
        ) : (
          <BellIcon className="w-6 h-6" />
        )}
        {unreadCount > 0 && (
          <span className={`absolute -top-1 -right-1 flex items-center justify-center min-w-[20px] h-5 px-1 text-xs font-bold text-white rounded-full ${
            shouldPulse ? 'bg-red-500 animate-pulse' : 'bg-orange-500'
          }`}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Desktop dropdown (>= sm) */}
      {isOpen && !isMobile && (
        <div className="hidden sm:flex absolute right-0 mt-2 w-96 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 z-50 overflow-hidden flex-col max-h-[calc(100vh-5rem)]">
          <AlertsList {...listProps} />
        </div>
      )}

      {/* Mobile drawer (< sm) — mounted only on mobile, see isMobile above */}
      <Transition show={isOpen && isMobile} as={Fragment}>
        <Dialog onClose={() => setIsOpen(false)} className="relative z-50 sm:hidden">
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
          </Transition.Child>
          <div className="fixed inset-0 flex justify-end">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-200 transform"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-200 transform"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="w-full max-w-sm bg-white dark:bg-gray-800 flex flex-col h-full">
                <AlertsList {...listProps} />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  )
}
